/* eslint-disable no-console */
import { Component, isDevMode } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { DateUtils } from "src/utils/date-utils";
import { DataTableColumn } from "../../shared/types/ngx-datatable-column";
import { BaseActionDialogComponent, BaseActionDialogData } from "../base-action-dialog/base-action-dialog.component";
import { BaseV2Params } from "./base-component-v2.types";

type MessageType = "success" | "error" | "info";

/**
 * Versão tipada do BaseComponent.
 *
 * Migração para o BaseV2Component será aos poucos.
 * Adicione o necessário a medida que precisar
 */

@Component({
    selector: "app-base-component-v2",
    templateUrl: "./base-component-v2.component.html",
})
export class BaseV2Component<T = unknown, P extends BaseV2Params = BaseV2Params> {
    //variáveis para datatable
    rows: T[] = [];
    params: P;
    columns: DataTableColumn[] = [];

    loadingIndicator: boolean = false;

    //paginação
    public length: number = 0;
    public pageSize: number = 100;
    public pageSizeOptions: number[] = [100, 200, 300, 500];

    //Componente de loading de carregamento
    loadingBaseComponent!: MatDialogRef<LoadingBaseComponent>;

    constructor(public dialogLoading: MatDialog, public snackBar: MatSnackBar) {
        // Inicialização padrão para evitar erro de undefined
        this.params = { page: 1, per_page: this.pageSize } as P;

        this.rows = [];
    }

    /**
     * Abre loading
     */
    openLoading(): void {
        this.loadingBaseComponent = this.dialogLoading.open(LoadingBaseComponent, {
            width: "150px",
            disableClose: true,
        });
    }

    /**
     * Fecha loading
     */
    closeLoading(): void {
        this.loadingBaseComponent.close();
    }

    /**
     * Debuga o formulário. Printa os campos inválidos e seus valores.
     * @param form - Formulário a ser debugado
     */
    debugTestForm(form: FormGroup): void {
        if (!isDevMode()) {
            return;
        }

        console.log("=== DEBUG DO FORMULÁRIO ===");
        console.log("Form status:", form.status);
        console.log("Form errors:", form.errors);

        Object.keys(form.controls).forEach((key) => {
            const control = form.get(key);
            if (control && control.invalid) {
                console.log(`❌ Campo "${key}":`, {
                    value: control.value,
                    errors: control.errors,
                    touched: control.touched,
                    dirty: control.dirty,
                    disabled: control.disabled,
                });
            } else {
                console.log(`✅ Campo "${key}": válido`, {
                    value: control?.value,
                    errors: control?.errors,
                    touched: control?.touched,
                    dirty: control?.dirty,
                    disabled: control?.disabled,
                });
            }
        });
    }

    /**
     * Formata uma data para exibição no formato brasileiro (DD/MM/AAAA)
     * @param date Data no formato ISO ou string
     * @returns Data formatada ou string vazia
     */

    displayFnDatePTBR(date: string | null): string {
        return DateUtils.formatDisplayDate(date);
    }

    /**
     * Exibe uma mensagem de sucesso, erro ou informação
     * @param message - Mensagem a ser exibida
     * @param type - Tipo de mensagem (success, error, info)
     */
    showSnackMessage(message: string, type: MessageType = "success"): void {
        const config = {
            duration: 3000,
            verticalPosition: "top" as const,
            horizontalPosition: "center" as const,
            panelClass: `snackbar-${type}`,
        };

        this.snackBar.open(message, "Fechar", config);
    }

    /**
     * PROSP-564/632/633: exibe MODAL no centro (BaseActionDialog) em vez de snackbar —
     * espelha o BaseComponent (v1). Usado para explicar o motivo (ex.: FK/vínculos) com a
     * mensagem do backend, no mesmo padrão adotado no 632/633.
     * @param message mensagem (normalmente error.error.message do backend)
     * @param title título do modal
     * @param type tipo visual do modal (padrão "error"); ex.: "warning" para sucesso-com-ressalva
     */
    showErrorModal(
        message: string,
        title: string = "Não foi possível concluir",
        type: "error" | "warning" | "info" = "error"
    ): void {
        const data: BaseActionDialogData = {
            title,
            message,
            type,
            showCancel: false,
            confirmText: "Entendi",
            callback: () => {
                /* apenas fecha */
            },
        };

        this.dialogLoading.open(BaseActionDialogComponent, {
            width: "450px",
            disableClose: false,
            data,
        });
    }
}

// Componente de loading de carregamento
@Component({
    selector: "loading-dialog",
    template: "<mat-spinner></mat-spinner> Carregando...",
})
export class LoadingBaseComponent {
    constructor() {}
}
